import { INSTRUCTIONS_ORDER } from 'app/src/engine/Instruction';
import { useGameStateContext } from 'app/src/game/GameStateContext';
import { GameFinishedReason } from 'app/src/game/GameStateStore';
import { useInstructionsArchiveContext } from 'app/src/game/InstructionsArchiveContext';
import { useLevelContext } from 'app/src/game/LevelContext';
import { useProgramStoreContext } from 'app/src/game/ProgramStoreContext';
import LocalStorageManager from 'app/src/utils/LocalStorageManager';
import useValueChanged from 'app/src/utils/useValueChanged';
import { useEffect } from 'react';

export default function ProgramChangeController(): null {
	const { program, setProgram } = useProgramStoreContext();
	const { instructionCounts, setInstructionCount } = useInstructionsArchiveContext();
	const { reset, gameStateStore } = useGameStateContext();
	const { currentLevel } = useLevelContext();

	// On initial load of a level, check local storage for a saved program and set
	// it.
	useEffect(() => {
		if (currentLevel === null) {
			return;
		}

		const savedProgram = LocalStorageManager.sharedInstance.getSavedProgram(currentLevel);
		if (savedProgram) {
			setProgram(savedProgram);

			for (const instructionType of INSTRUCTIONS_ORDER) {
				const countInProgram = savedProgram.filter((i) => i === instructionType).length;
				const countInArchive = instructionCounts.get(instructionType);
				if (typeof countInArchive === 'number') {
					setInstructionCount(
						instructionType,
						countInArchive > countInProgram ? countInArchive - countInProgram : 0,
					);
				}
			}
		}
	}, [currentLevel]); // eslint-disable-line react-hooks/exhaustive-deps

	// Save program changes to local storage.
	useEffect(() => {
		if (currentLevel === null) {
			return;
		}

		LocalStorageManager.sharedInstance.saveProgram(currentLevel, program);
	}, [currentLevel, program]);

	// If the user modifies the program while its executing, reset.
	const programChanged = useValueChanged(program);
	useEffect(() => {
		if (programChanged && gameStateStore.gameFinishedReason !== GameFinishedReason.Reset) {
			reset();
		}
	}, [gameStateStore.gameFinishedReason, programChanged, reset]);

	return null;
}
